import classNames from "classnames";
import { useEffect, useRef } from "react";

export const useTransitionDuration = (): SVGPathElement[] => {
    const pathsRef = useRef<SVGPathElement[]>([]);
    const prevTimeStamp = useRef<number>();

    useEffect(() => {
        const now = Date.now();
        let updated = false;

        pathsRef.current.forEach((path) => {
            if (!path) {
                return;
            }

            updated = true;
            const pathStyle = path.style;
            pathStyle.transitionDuration = ".3s, .3s, .3s, .06s";

            if (prevTimeStamp.current && now - prevTimeStamp.current < 100) {
                pathStyle.transitionDuration = "0s, 0s";
            }
        });

        if (updated) {
            prevTimeStamp.current = Date.now();
        }
    });

    return pathsRef.current;
};

export const LProgressLine = ({
    className,
    percent,
    prefixCls = "rc-progress",
    strokeColor,
    strokeLinecap = "round",
    strokeWidth = 1,
    style,
    trailColor,
    trailWidth,
    transition,
    ...restProps
}: ProgressProps): JSX.Element => {
    // eslint-disable-next-line no-param-reassign
    delete restProps.gapPosition;
    const percentList = Array.isArray(percent) ? percent : [percent];
    const strokeColorList = Array.isArray(strokeColor) ? strokeColor : [strokeColor];

    const paths = useTransitionDuration();

    const center = strokeWidth / 2;
    const right = 100 - strokeWidth / 2;
    const pathString = `M ${strokeLinecap === "round" ? center : 0},${center}
           L ${strokeLinecap === "round" ? right : 100},${center}`;
    const viewBoxString = `0 0 100 ${strokeWidth}`;
    let stackPtg = 0;
    return (
        <svg
            className={classNames(`${prefixCls}-line`, className)}
            viewBox={viewBoxString}
            preserveAspectRatio="none"
            style={style}
            {...restProps}
        >
            <path
                className={`${prefixCls}-line-trail`}
                d={pathString}
                strokeLinecap={strokeLinecap}
                stroke={trailColor}
                strokeWidth={trailWidth ?? strokeWidth}
                fillOpacity="0"
            />
            {percentList.map((ptg: number, index) => {
                let dashPercent = 1;
                switch (strokeLinecap) {
                    case "round":
                        dashPercent = 1 - strokeWidth / 100;
                        break;
                    case "square":
                        dashPercent = 1 - strokeWidth / 2 / 100;
                        break;
                    default:
                        dashPercent = 1;
                        break;
                }
                const pathStyle = {
                    strokeDasharray: `${ptg * dashPercent}px, 100px`,
                    strokeDashoffset: `-${stackPtg}px`,
                    transition:
                        transition ?? "stroke-dashoffset 0.3s ease 0s, stroke-dasharray .3s ease 0s, stroke 0.3s linear"
                };
                const color = strokeColorList[index] ?? strokeColorList[strokeColorList.length - 1];
                stackPtg += ptg;
                return (
                    <path
                        key={index}
                        className={`${prefixCls}-line-path`}
                        d={pathString}
                        strokeLinecap={strokeLinecap}
                        stroke={color as string}
                        strokeWidth={strokeWidth}
                        fillOpacity="0"
                        ref={(elem) => {
                            // https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
                            // React will call the ref callback with the DOM element when the component mounts,
                            // and call it with `null` when it unmounts.
                            // Refs are guaranteed to be up-to-date before componentDidMount or componentDidUpdate fires.

                            paths[index] = elem!;
                        }}
                        style={pathStyle}
                    />
                );
            })}
        </svg>
    );
};

export interface ProgressProps {
    id?: string;
    strokeWidth?: number;
    trailWidth?: number;
    className?: string;
    percent: number;
    strokeColor?: StrokeColorType;
    trailColor?: string;
    strokeLinecap?: StrokeLinecapType;
    prefixCls?: string;
    style?: React.CSSProperties;
    gapDegree?: number;
    gapPosition?: GapPositionType;
    transition?: string;
    onClick?: React.MouseEventHandler;
    steps?: number | { count: number; space: number };
}

export type BaseStrokeColorType = string | Record<string, string>;

export type StrokeColorType = BaseStrokeColorType | BaseStrokeColorType[];

export type GapPositionType = "top" | "right" | "bottom" | "left";

export type StrokeLinecapType = "round" | "butt" | "square";
